/**
 * 「帮我写脚本」调用 + 回填纪律(0924 车 D 二期,契约 §3.4)。
 *
 * 页面按钮、样片模板、追爆蓝图三处都走这一个 hook → 同一个端点 `POST /video/real/script`
 * (军师「当编剧」也打这个端点,单一源)。
 *
 * ★回填纪律:脚本回来时框里若仍是「空 / 未改动的上一份种子 / 发出去的那句想法」,直接填;
 *   否则(用户在等待期间自己改了框)**不覆盖**,把结果挂成待确认(`pending`),
 *   用户点「用这版替换」才进框 —— 框里是用户的。
 * ★填进去之后「框里是什么就发什么」不变:脚本只是帮用户起稿,提交仍原样直发。
 */
import { useRef, useState } from "react";

import {
  videoService,
  type RealScriptRequest,
  type RealScriptResponse,
} from "../../../api/videoService";
import { isReplaceableRealBrief } from "../../../model/realPersonIntake";
import { errorText } from "@/shared/lib/apiErrors";

export interface RealScriptWriter {
  writing: boolean;
  error: string | null;
  pending: RealScriptResponse | null;
  run: (req: RealScriptRequest, sentText: string) => Promise<void>;
  acceptPending: () => void;
  dismissPending: () => void;
}

export function useRealScriptWriter(opts: {
  getText: () => string;
  getSeed: () => string | null;
  apply: (r: RealScriptResponse) => void;
}): RealScriptWriter {
  const [writing, setWriting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<RealScriptResponse | null>(null);
  const busy = useRef(false);
  const optsRef = useRef(opts);
  optsRef.current = opts;

  const run = async (req: RealScriptRequest, sentText: string) => {
    if (busy.current) return;
    busy.current = true;
    setWriting(true);
    setError(null);
    setPending(null);
    try {
      const r = await videoService.writeRealScript(req);
      if (!r?.prompt?.trim()) {
        setError("这次没写出来,换个说法再试一次");
        return;
      }
      const { getText, getSeed, apply } = optsRef.current;
      const now = getText();
      if (isReplaceableRealBrief(now, getSeed()) || now.trim() === sentText.trim()) {
        apply(r);
      } else {
        setPending(r);
      }
    } catch (e) {
      setError(errorText(e, { hiddenDescription: "写脚本失败,请重试" }));
    } finally {
      busy.current = false;
      setWriting(false);
    }
  };

  return {
    writing,
    error,
    pending,
    run,
    acceptPending: () => {
      if (pending) optsRef.current.apply(pending);
      setPending(null);
    },
    dismissPending: () => setPending(null),
  };
}
